import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import isDifferent from 'deep-diff';
import { useQuery, usePreventWindowUnload } from '@panel/commons/hooks';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { useTranslation } from 'react-i18next';
import { useSnackbar, SNACKBAR_TYPE } from '@panel/commons/lib/snackbar';
import { WrapperContainer } from '../../Wrappers/WrapperContainer';
import { Step1Module1Selectors } from '../../../ducks/step1Module1';
import { Videos } from './videos/videos';
import { Videos360 } from './videos/videos360';
import { ImageSection, PlansSection } from './images';
import { updateLastStep } from '../../../ducks/stepper';
import { step2Schema, Step2Module3Schema } from './validations';
import {
  getMultimedias,
  validate,
  stepData,
  getMultimediaState,
  getSavedMedia,
  cleanMultimedia,
  MultimediaCollection
} from '../../../ducks/step2';
import { savePostingDB } from '../../../ducks/savePosting/actions';
import { sendEvent, PUBLICATION_ABANDON, track } from '../../../utils/trackingEvent';
import { useStepperCtx } from '../../stepper';

const checkIsEdited = (obj1, obj2, compare = (path, key) => key === 'id') => {
  return isDifferent(obj1, obj2, compare);
};

const SectionsWrapper = WrapperContainer(({ setId, loading }) => {
  const [validateVideo, setValidateVideo] = useState(false);
  const [validate360, setValidate360] = useState(false);
  const [emptyInputs, setEmptyInputs] = useState({ emptyVideo: false, empty360: false });
  const { videos, tour360, images, plans, processing } = useSelector(getMultimediaState);
  const { originalMultimedia } = useSelector(getSavedMedia);
  const { ...validationData } = useSelector(validate);
  const { ...formData } = useSelector(stepData);
  const {
    postingStatuses,
    operationTypeId,
    propertyTypeId,
    subPropertyTypeId,
    propertyTypes,
    propertySubTypes
  } = useSelector(Step1Module1Selectors.getInfoEvents);
  const { setToggleSnack, setSnackText, setSnackType } = useSnackbar();
  const { setPreventTransition, nextStepperRoute } = useStepperCtx();
  const [postingId] = useQuery(useLocation(), ['postingId']);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { register, errors, trigger, setValue, getValues, handleSubmit, formState, clearErrors } = useForm({
    resolver: yupResolver(Step2Module3Schema),
    context: { videos, tour360, validateVideo, validate360 },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    shouldFocusError: true
  });
  const formRef = useRef(null);
  const editionImg = originalMultimedia ? originalMultimedia.pictures : null;
  const editionPlans = originalMultimedia ? originalMultimedia.plan : null;
  const editionVideos = originalMultimedia ? originalMultimedia.videos : null;
  const editionTour360 = originalMultimedia ? originalMultimedia.tour360 : null;
  const mediaChangeInEdition =
    (!originalMultimedia && (images.length > 0 || plans.length > 0 || videos.length > 0 || tour360.length > 0)) ||
    (originalMultimedia &&
      (checkIsEdited(images, { images: editionImg }) ||
        checkIsEdited(plans, { plans: editionPlans }) ||
        checkIsEdited(videos, { videos: editionVideos }) ||
        checkIsEdited(tour360, { tour360: editionTour360 })));

  const notifyAbandon = () => {
    if (postingStatuses.includes('DRAFT')) {
      track(PUBLICATION_ABANDON, operationTypeId, postingId);
    }
  };

  useEffect(() => {
    if (postingId) {
      dispatch(getMultimedias(postingId));
    }
  }, [dispatch, postingId]);

  useEffect(() => {
    setId('step2');
  }, [setId]);

  useEffect(() => {
    window.addEventListener('beforeunload', notifyAbandon);
    return () => {
      window.removeEventListener('beforeunload', notifyAbandon);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postingStatuses]);

  useEffect(() => {
    if (formState.isDirty || mediaChangeInEdition || processing) {
      setPreventTransition(formRef);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.isDirty, mediaChangeInEdition, processing]);

  const onSubmit = async (data, evt) => {
    const { nativeEvent } = evt;
    setEmptyInputs({ emptyVideo: false, empty360: false });
    evt.preventDefault();
    try {
      await step2Schema.validate(validationData);
      const payload = {
        body: { ...formData, postingId },
        step: 'STEP_MULTIMEDIA'
      };
      const saveAndExit = nativeEvent.submitter && nativeEvent.submitter.id === 'saveAndExit';
      const savedFromModal = nativeEvent.detail && nativeEvent.detail.submitter === 'saveAndRedirect';
      const goBack = nativeEvent.detail && nativeEvent.detail.goBack;
      const path = goBack || nextStepperRoute || '/publicador/extras';

      const propertyObj = { list: propertyTypes, id: propertyTypeId };
      const subPropertyObj = { list: propertySubTypes, id: subPropertyTypeId };

      sendEvent(
        'Multimedia',
        saveAndExit ? 'Guardar y salir' : 'Continuar',
        operationTypeId,
        propertyObj,
        subPropertyObj
      );
      if (saveAndExit) {
        notifyAbandon();
      }
      dispatch(cleanMultimedia(MultimediaCollection.IMAGE));
      dispatch(cleanMultimedia(MultimediaCollection.PLAN));
      dispatch(savePostingDB(payload, path, saveAndExit, savedFromModal));
      dispatch(updateLastStep('2', '1'));
    } catch (validationError) {
      const validationPath = validationError.path;
      if (['pictures', 'plan'].includes(validationPath)) {
        setSnackText(t(validationError.errors[0]));
        setSnackType(SNACKBAR_TYPE.DANGER);
        setToggleSnack(10000);
      }
    }
  };

  usePreventWindowUnload(formState.isDirty || mediaChangeInEdition);

  return (
    <form data-repa='step2-imagesVideosForm' id='step2' ref={formRef} onSubmit={handleSubmit(onSubmit)}>
      <fieldset data-repa='step2-fieldset' disabled={loading}>
        <ImageSection data-repa='step2-imagesSection' />
        <Videos
          data-repa='step2-videos'
          emptyInputs={emptyInputs}
          setEmptyInputs={setEmptyInputs}
          register={register}
          errors={errors}
          setValue={setValue}
          trigger={trigger}
          getValues={getValues}
          setValidateVideo={setValidateVideo}
          validateVideo={validateVideo}
          clearErrors={clearErrors}
        />
        <PlansSection data-repa='step2-plansSection' />
        <Videos360
          data-repa='step2-360'
          emptyInputs={emptyInputs}
          setEmptyInputs={setEmptyInputs}
          register={register}
          errors={errors}
          setValue={setValue}
          trigger={trigger}
          getValues={getValues}
          setValidateVideo={setValidate360}
          validateVideo={validate360}
        />
      </fieldset>
    </form>
  );
});

export { SectionsWrapper };
